import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import Slider from 'react-slick';
import { Icon, Loader, Text, tokens } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/tracking';
import { useFeaturedInsightsContent } from '@hotjar/webapp/domains/activation/featured-insights';
import { FeaturedInsightsSlide as Slide } from './FeaturedInsightsSlide';
import { NextButton, PrevButton } from './components/FeaturedInsightsArrowButtons';

const LoaderContainer = styled.div`
  margin: 20px auto 40px;
`;

const Container = styled.div`
  margin-bottom: ${tokens.spacer.x3};
`;

const Title = styled(Text)`
  margin-bottom: 20px;
`;

const CardContainer = styled.div`
  position: relative;
  padding: 18px 18px 0;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};
  overflow: hidden;
`;

const VerticalPrevButton = styled.button`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(50, 79, 190, 0.3) 0%, rgba(172, 189, 255, 0) 100%);
  border: none;
  opacity: 0;
  transition: opacity linear 200ms;
`;

const VerticalArrowLeftIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-${tokens.spacer.x1});
  width: ${tokens.spacer.x3};
  height: ${tokens.spacer.x3};
  background-color: ${tokens.color.ui_background};
  border-radius: ${tokens.spacer.x3};
  transition: margin linear 200ms;
`;

const VerticalLeftButtonContainer = styled.div<{ disabled: boolean }>`
  position: absolute;
  display: ${({ disabled }) => (disabled ? 'none' : 'block')};
  top: 0;
  left: 0;
  width: ${tokens.spacer.x8pt5};
  height: 100%;
  z-index: 1;

  &:hover ${VerticalPrevButton} {
    opacity: 1;
  }

  &:hover ${VerticalArrowLeftIcon} {
    margin-right: -${tokens.spacer.x3pt5};
    pointer-events: none;
  }
`;

const VerticalNextButton = styled(VerticalPrevButton)`
  transform: rotate(-180deg);
`;

const VerticalArrowRightIcon = styled(VerticalArrowLeftIcon)`
  right: inherit;
  left: 100%;
`;

const VerticalRightButtonContainer = styled(VerticalLeftButtonContainer)`
  left: inherit;
  right: 0;

  &:hover ${VerticalNextButton} {
    opacity: 1;
  }

  &:hover ${VerticalArrowRightIcon} {
    margin-left: -${tokens.spacer.x3pt5};
    pointer-events: none;
  }
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 18px;
`;

const StyledIcon = styled(Icon)`
  display: inline-block;
  width: ${tokens.spacer.x3pt5};
  height: ${tokens.spacer.x3pt5};
  align-self: center;
  margin-right: 18px;
  color: ${tokens.color.primary_cta};
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SlideContainer = styled.div`
  width: calc(100% + 18px * 2);
  margin-left: -18px;
`;

const SliderDot = styled.div`
  width: ${tokens.spacer.x1};
  height: ${tokens.spacer.x1};
  background-color: ${tokens.color.neutral_500};
  border-radius: ${tokens.spacer.x1};
  cursor: pointer;
`;

const StyledSlider = styled(Slider)`
  .slick-slide {
    padding-left: 18px;

    &:last-child {
      padding-right: 18px;
    }
  }
`;

const SliderPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
`;

const DotsPagination = styled.ul<{ isLastSlideActive: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin: 0;
  padding: 0;
  list-style: none;
  gap: ${tokens.spacer.x1};

  .slick-active:is(li) ${SliderDot} {
    background-color: ${tokens.color.primary_cta};
  }

  & li:last-of-type ${SliderDot} {
    /* Since the slider shows 1.1 slides, the last page turns up as X.9 instead of an integer.
    This forces the last dot to adopt the active color. */
    background-color: ${({ isLastSlideActive }) =>
      isLastSlideActive ? tokens.color.primary_cta : tokens.color.neutral_500};
  }
`;

const InvisibleArrow = styled.div`
  display: none;
`;

export const FeaturedInsights = (): JSX.Element | null => {
  const { t } = useTranslation();
  const sliderRef = useRef<Slider>(null);
  const track = useTrack();
  const [activeSlide, setActiveSlide] = useState(0);
  const { data: featuredInsightsSlideContent, isLoading } = useFeaturedInsightsContent();
  const isFirstSlideActive = activeSlide === 0;
  const isLastSlideActive = !!(
    featuredInsightsSlideContent && activeSlide === featuredInsightsSlideContent.insights.length - 1
  );

  const onPrevClick = () => {
    if (!isFirstSlideActive) {
      sliderRef.current?.slickPrev();
    }
  };

  const onNextClick = () => {
    if (!isLastSlideActive) {
      sliderRef.current?.slickNext();
    }
  };

  const handleBeforeChange = (current: number, next: number) => {
    if (current !== next) {
      const prevOrNextSlide = next > current ? 'next' : 'previous';

      track.click('Featured Insights', `Click ${prevOrNextSlide} insight`, {});

      setActiveSlide(Math.round(next));
    }
  };

  const handleSlideClick = (slideId: string) => {
    track.click('Featured Insights', `Click ${slideId} insight`, {});
  };

  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader size="m" />
      </LoaderContainer>
    );
  }

  return featuredInsightsSlideContent?.insights?.length ? (
    <Container>
      <Title as="h2" textType="header5" textColor="text_default">
        {t('activation:space-overview.for-you.featured-insights.title')}
      </Title>
      <CardContainer>
        <VerticalLeftButtonContainer disabled={isFirstSlideActive}>
          <VerticalPrevButton onClick={onPrevClick} />
          <VerticalArrowLeftIcon icon="arrow_left_alt" />
        </VerticalLeftButtonContainer>
        <VerticalRightButtonContainer disabled={isLastSlideActive}>
          <VerticalNextButton onClick={onNextClick} />
          <VerticalArrowRightIcon icon="arrow_right_alt" />
        </VerticalRightButtonContainer>
        <Header>
          <StyledIcon icon="star_empty" />
          <CopyContainer>
            <Text as="h4" textType="header5">
              {t('activation:space-overview.for-you.featured-insights.header.title')}
            </Text>
            <Text textColor="text_light">
              {t('activation:space-overview.for-you.featured-insights.header.subtitle')}
            </Text>
          </CopyContainer>
        </Header>
        <SlideContainer>
          <StyledSlider
            dots
            infinite={false}
            speed={500}
            slidesToScroll={1}
            slidesToShow={1.1}
            prevArrow={<InvisibleArrow />}
            nextArrow={<InvisibleArrow />}
            ref={sliderRef}
            beforeChange={handleBeforeChange}
            appendDots={(dots) => (
              <SliderPaginationContainer>
                <PrevButton isFirstSlideActive={isFirstSlideActive} onClick={onPrevClick} />
                <DotsPagination isLastSlideActive={isLastSlideActive}>{dots}</DotsPagination>
                <NextButton isLastSlideActive={isLastSlideActive} onClick={onNextClick} />
              </SliderPaginationContainer>
            )}
            customPaging={() => <SliderDot />}
          >
            {featuredInsightsSlideContent?.insights.map((slide) => (
              <Slide
                key={slide.details.id}
                details={slide.details}
                type={slide.type}
                onClick={handleSlideClick}
              />
            ))}
          </StyledSlider>
        </SlideContainer>
      </CardContainer>
    </Container>
  ) : null;
};
